import { useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import api from "../lib/api";
import { uploadToCloudinary, uploadMultipleToCloudinary, MODERATION_ERROR } from "../lib/cloudinary";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Image, Plus, X, Trash2, Loader2 } from "lucide-react";

const CreatePollPage = () => {
  const navigate = useNavigate();
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState([
    { text: "", mediaFile: null, mediaPreview: null },
    { text: "", mediaFile: null, mediaPreview: null },
  ]);
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const imageInputRef = useRef(null);

  const addOption = () => {
    if (options.length >= 10) {
      toast.message("Maximum 10 options allowed.");
      return;
    }
    setOptions((prev) => [...prev, { text: "", mediaFile: null, mediaPreview: null }]);
  };

  const removeOption = (index) => {
    if (options.length <= 2) {
      toast.message("At least 2 options are required.");
      return;
    }
    setOptions((prev) => prev.filter((_, idx) => idx !== index));
  };

  const updateOptionText = (index, value) => {
    setOptions((prev) => prev.map((option, idx) => (idx === index ? { ...option, text: value } : option)));
  };

  const updateOptionMedia = (index, file) => {
    const preview = file ? URL.createObjectURL(file) : null;
    setOptions((prev) =>
      prev.map((option, idx) => (idx === index ? { ...option, mediaFile: file, mediaPreview: preview } : option))
    );
  };

  const clearOptionMedia = (index) => {
    setOptions((prev) =>
      prev.map((option, idx) => (idx === index ? { ...option, mediaFile: null, mediaPreview: null } : option))
    );
  };

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 5) {
      toast.error("Select between 1 and 5 images.");
      return;
    }
    setImageFiles(files);
    setImagePreviews(files.map((f) => URL.createObjectURL(f)));
  };

  const clearAttachment = () => {
    setImageFiles([]);
    setImagePreviews([]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!question.trim()) {
      toast.error("Add a clear question.");
      return;
    }

    const optionTexts = options.map((option) => option.text.trim());
    if (optionTexts.some((text) => !text)) {
      toast.error("Fill in every answer option.");
      return;
    }
    if (optionTexts.length < 2) {
      toast.error("Add at least two answer options.");
      return;
    }

    try {
      setSubmitting(true);

      let questionMediaUrls = [];
      let questionMediaType = null;

      if (imageFiles.length > 0) {
        setUploadProgress("Uploading images...");
        questionMediaUrls = await uploadMultipleToCloudinary(imageFiles, (done, total) => {
          setUploadProgress(`Uploading image ${done}/${total}...`);
        });
        questionMediaType = "image";
      }

      const optionMediaUrls = {};
      const optionMediaEntries = options
        .map((opt, idx) => (opt.mediaFile ? { idx, file: opt.mediaFile } : null))
        .filter(Boolean);

      if (optionMediaEntries.length > 0) {
        for (let i = 0; i < optionMediaEntries.length; i++) {
          const entry = optionMediaEntries[i];
          setUploadProgress(`Uploading option image ${i + 1}/${optionMediaEntries.length}...`);
          const url = await uploadToCloudinary(entry.file);
          optionMediaUrls[String(entry.idx)] = url;
        }
      }

      setUploadProgress("Creating poll...");

      const payload = {
        question: question.trim(),
        options: optionTexts,
        allow_multiple: allowMultiple,
        is_private: isPrivate,
      };

      if (questionMediaUrls.length > 0) {
        payload.question_media_type = questionMediaType;
        payload.question_media_urls = questionMediaUrls;
      }

      if (Object.keys(optionMediaUrls).length > 0) {
        payload.option_media_urls = optionMediaUrls;
      }

      const response = await api.post("/polls/with-urls", payload);
      toast.success("Poll created.");
      navigate(`/poll/${response.data.id}`);
    } catch (error) {
      const msg = error?.message === MODERATION_ERROR
        ? MODERATION_ERROR
        : error?.message === "Image upload failed"
          ? "Image upload failed. Please try again."
          : "Unable to create poll. Please check your inputs.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
      setUploadProgress("");
    }
  };

  return (
    <div className="space-y-6" data-testid="create-poll-page">
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold" data-testid="create-poll-title">
          Create a poll
        </h2>
        <p className="text-sm text-muted-foreground" data-testid="create-poll-subtitle">
          Ask a question, then add answers or attach media.
        </p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit} data-testid="create-poll-form">
        <div className="space-y-3">
          <label className="text-sm font-medium" data-testid="create-poll-question-label">
            Question
          </label>
          <Textarea
            rows={3}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="Ask something the crowd can answer quickly."
            data-testid="create-poll-question-input"
          />
          <div className="flex items-center gap-2" data-testid="create-poll-attachments">
            <input
              type="file"
              accept="image/*"
              multiple
              ref={imageInputRef}
              onChange={handleImageChange}
              className="hidden"
              data-testid="create-poll-image-input"
            />
            <button
              type="button"
              className="h-9 w-9 rounded-full border border-border flex items-center justify-center"
              onClick={() => imageInputRef.current?.click()}
              data-testid="create-poll-image-button"
            >
              <Image className="h-4 w-4" />
            </button>
            {imageFiles.length > 0 && (
              <button
                type="button"
                className="ml-2 flex items-center gap-1 text-xs text-muted-foreground"
                onClick={clearAttachment}
                data-testid="create-poll-clear-attachment"
              >
                <X className="h-3 w-3" />
                Clear images
              </button>
            )}
          </div>
          {imagePreviews.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="create-poll-image-previews">
              {imagePreviews.map((src, index) => (
                <img
                  key={index}
                  src={src}
                  alt={`Preview ${index + 1}`}
                  className="h-20 w-20 rounded-lg object-cover border border-border"
                  data-testid={`create-poll-image-preview-${index}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="space-y-3" data-testid="create-poll-options">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium" data-testid="create-poll-options-label">
              Answer options
            </label>
            <button
              type="button"
              className="h-8 w-8 rounded-full border border-border flex items-center justify-center"
              onClick={addOption}
              data-testid="create-poll-add-option"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-3">
            {options.map((option, index) => (
              <div key={index} className="space-y-2" data-testid={`create-poll-option-row-${index}`}>
                <div className="flex items-center gap-2">
                  <Input
                    value={option.text}
                    onChange={(event) => updateOptionText(index, event.target.value)}
                    placeholder={`Option ${index + 1}`}
                    data-testid={`create-poll-option-input-${index}`}
                  />
                  <div className="flex items-center gap-2">
                    <label
                      htmlFor={`option-media-${index}`}
                      className="h-10 w-10 rounded-full border border-border flex items-center justify-center cursor-pointer"
                      data-testid={`create-poll-option-media-button-${index}`}
                    >
                      <Image className="h-4 w-4" />
                    </label>
                    <input
                      id={`option-media-${index}`}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(event) => updateOptionMedia(index, event.target.files?.[0] || null)}
                      data-testid={`create-poll-option-media-input-${index}`}
                    />
                    {option.mediaFile && (
                      <button
                        type="button"
                        className="h-10 w-10 rounded-full border border-border flex items-center justify-center"
                        onClick={() => clearOptionMedia(index)}
                        data-testid={`create-poll-option-media-clear-${index}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      type="button"
                      className="h-10 w-10 rounded-full border border-border flex items-center justify-center"
                      onClick={() => removeOption(index)}
                      data-testid={`create-poll-remove-option-${index}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {option.mediaPreview && (
                  <img
                    src={option.mediaPreview}
                    alt={`Option ${index + 1} preview`}
                    className="h-20 w-20 rounded-lg object-cover border border-border"
                    data-testid={`create-poll-option-media-preview-${index}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {imageFiles.length > 0 && (
          <div className="text-xs text-muted-foreground" data-testid="create-poll-image-note">
            Question images appear above your answer options.
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4" data-testid="create-poll-footer">
          <Button type="submit" disabled={submitting} data-testid="create-poll-submit">
            {submitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {uploadProgress || "Publishing..."}
              </span>
            ) : (
              "Publish poll"
            )}
          </Button>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-2" data-testid="create-poll-multi-toggle-row">
              <Switch checked={allowMultiple} onCheckedChange={setAllowMultiple} data-testid="create-poll-multi-toggle" />
              <span data-testid="create-poll-multi-label">Multiple answers</span>
            </div>
            <div className="flex items-center gap-2" data-testid="create-poll-privacy-toggle-row">
              <Switch checked={isPrivate} onCheckedChange={setIsPrivate} data-testid="create-poll-privacy-toggle" />
              <span data-testid="create-poll-privacy-label">Private</span>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreatePollPage;
